import React from 'react';
import { connect } from 'react-redux';
import { Col, Row } from 'antd';
import { motion } from 'framer-motion';
import { css, StyleSheet } from 'aphrodite';
import { Box, Typography } from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import { MuiOtpInput } from 'mui-one-time-password-input';
import { Userpilot } from 'userpilot';

import BasePage from '../base';
import TextBase from '../../common/text';
import ButtonGradient from '../../common/button-gradient';
import CustomSpinner from '../../common/CustomSpinner';
import { Colors } from '../../themes/Colors';
import { SVGImageComponentReact } from '../../themes/Images';
import { animate } from '../../utils/defined';
import { ROUTER_NAME } from '../../router/routers';
import { MESSAGE_TYPE, messageManager, getValueFromObjectByKeys, translate } from '../../helper';
import { UserAction } from '../../redux/action';
import { setPermissions } from '../../redux/reducers/permissionsSlice';
import { getFirstPermittedRoute } from '../../utils/getFirstPermittedRoute';
import LocalStorage from '../../defined/localStorage';
import WithNavigate from '../../component/WithNavigate';
import { REQUEST_TYPE, REQUEST_SUBTYPE } from '../../redux/action/ActionType';

const styles = StyleSheet.create({
    flex: {
        display: 'flex',
        justifyContent: 'space-between',
        marginLeft: 'var(--sp-lg)',
        marginTop: 'var(--sp-lg)'
    }
});

const withLocationState = (WrappedComponent) => (props) => {
    const email = props.location?.state?.email;
    const fromLogin = props.location?.state?.fromLogin;
    const twoFaToken = props.location?.state?.twoFaToken;
    return <WrappedComponent {...props} email={email} fromLogin={fromLogin} twoFaToken={twoFaToken} />;
};

class Verify2FA extends BasePage {
    constructor(props) {
        super(props);
        this.state = {
            otp: '',
            seconds: 60,
            submitting: false,
            resending: false
        };
        this.resendTimerRef = null;
    }

    componentDidMount() {
        const { fromLogin, twoFaToken, navigate } = this.props;
        if (!fromLogin || !twoFaToken) {
            navigate(ROUTER_NAME.login);
            return;
        }
        this.startResendTimer();
    }

    componentWillUnmount() {
        if (this.resendTimerRef) {
            clearInterval(this.resendTimerRef);
        }
    }

    startResendTimer = () => {
        if (this.resendTimerRef) clearInterval(this.resendTimerRef);
        this.setState({ seconds: 60 });
        this.resendTimerRef = setInterval(() => {
            this.setState((prev) => {
                if (prev.seconds <= 1) {
                    clearInterval(this.resendTimerRef);
                    this.resendTimerRef = null;
                    return { seconds: 0 };
                }
                return { seconds: prev.seconds - 1 };
            });
        }, 1000);
    };

    handleOtpChange = (code) => {
        const next = (code || '').replace(/\D/g, '').slice(0, 6);
        this.setState({ otp: next });
    };

    handleResendOTP = async () => {
        const { seconds, resending } = this.state;
        const { resend2FAOTP, navigate, twoFaToken } = this.props;

        if (seconds !== 0 || resending) return;

        this.setState({ resending: true });
        try {
            const res = await resend2FAOTP({ two_fa_token: twoFaToken });
            const response = res?.data;
            if (response?.success) {
                messageManager({
                    type: MESSAGE_TYPE.success,
                    messageShow: translate('otpResentSuccess')
                });
                this.startResendTimer();
            } else if (response?.error === 'token_expired' || response?.error === 'token_required') {
                messageManager({
                    type: MESSAGE_TYPE.error,
                    messageShow: response?.message || translate('sessionExpired')
                });
                navigate(ROUTER_NAME.login);
            } else {
                messageManager({
                    type: MESSAGE_TYPE.error,
                    messageShow: response?.message || translate('otpResendFailed')
                });
            }
        } catch (err) {
            if (err?.error === 'token_expired' || err?.error === 'token_required') {
                navigate(ROUTER_NAME.login);
            }
            messageManager({
                type: MESSAGE_TYPE.error,
                messageShow: err?.message || translate('otpResendFailed')
            });
        } finally {
            this.setState({ resending: false });
        }
    };

    handleVerify = async () => {
        const { otp, submitting } = this.state;
        const { navigate, verify2FAOTP, dispatch, saveBranding, twoFaToken } = this.props;

        if (submitting || otp.length !== 6) return;

        this.setState({ submitting: true });
        try {
            const res = await verify2FAOTP({
                code: otp,
                two_fa_token: twoFaToken
            });
            const response = res?.data;

            if (response?.success) {
                // Set the flag in Redux
                const responseWithFlag = {
                    ...response,
                    user: {
                        ...response.user,
                        is_two_factor_auth_enabled: true
                    }
                };
                // Dispatch LOGIN success action so reducer saves token and userInfo
                dispatch({
                    type: REQUEST_TYPE.LOGIN + REQUEST_SUBTYPE.SUCCESS,
                    key: REQUEST_TYPE.LOGIN,
                    subType: REQUEST_SUBTYPE.SUCCESS,
                    data: { data: responseWithFlag }
                });

                dispatch(setPermissions(response?.permissions || []));
                saveBranding();

                Userpilot.identify(response?.user?.id, {
                    role: response?.user?.role,
                    email: response?.user?.email,
                    created_at: response?.user?.profile?.created_at,
                    user_verified: response?.user?.is_active,
                    subscription: true,
                    company: {
                        id: response?.user?.vendor?.id,
                        name: response?.user?.vendor?.business_name
                    }
                });

                localStorage.removeItem('SUBSCRIPTION_ERROR_DISMISSED');
                const isTeamMember = response?.user?.profile?.team_member;
                const permissions = response?.permissions || [];
                const defaultRoute = getFirstPermittedRoute({ isTeamMember, permissions });
                navigate(defaultRoute);
            } else if (response?.error === 'token_expired' || response?.error === 'token_required') {
                messageManager({
                    type: MESSAGE_TYPE.error,
                    messageShow: response?.message || translate('sessionExpired')
                });
                navigate(ROUTER_NAME.login);
            } else {
                messageManager({
                    type: MESSAGE_TYPE.error,
                    messageShow: response?.message || translate('invalidOTP')
                });
            }
        } catch (err) {
            if (err?.error === 'token_expired' || err?.error === 'token_required') {
                navigate(ROUTER_NAME.login);
            }
            messageManager({
                type: MESSAGE_TYPE.error,
                messageShow: err?.message || translate('verificationFailed')
            });
        } finally {
            this.setState({ submitting: false });
        }
    };

    renderContent() {
        const { otp, seconds, submitting, resending } = this.state;
        const { email, navigate } = this.props;

        return (
            <motion.div
                initial={animate.initial}
                animate={animate.animate}
                transition={animate.transition}
            >
                <div className={css(styles.flex)}>
                    <div
                        className="flex-row-center-center cursor-pointer"
                        onClick={() => {
                            navigate(ROUTER_NAME.login);
                        }}
                    >
                        <SVGImageComponentReact.IconArrowBack style={{ width: 'var(--fs-lg)', height: 'var(--fs-lg)' }} />
                    </div>
                    <div></div>
                </div>
                <div
                    className="max-width-100"
                    style={{
                        paddingTop: 'calc(var(--sp-lg)*4.7169)',
                        paddingInline: 'var(--sp-lg)',
                        paddingBottom: 'var(--sp-lg)'
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            marginBottom: 'var(--sp-md2)'
                        }}
                    >
                        <Box
                            sx={{
                                width: '56px',
                                height: '56px',
                                borderRadius: '50%',
                                backgroundColor: 'rgba(34, 197, 94, 0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}
                        >
                            <SecurityIcon sx={{ fontSize: '28px', color: '#22c55e' }} />
                        </Box>
                        <TextBase
                            fontWeight={600}
                            size={'calc(var(--fs-xl)*1.5094)'}
                            color={Colors.black}
                        >
                            {translate('twoFactorAuth')}
                        </TextBase>
                    </Box>

                    <TextBase size={'var(--fs-body)'} color={Colors.grey2}>
                        {translate('enter2FACode')}
                    </TextBase>
                    <TextBase size={'var(--fs-body)'} color={Colors.black} fontWeight={500}>
                        {email}
                    </TextBase>

                    <div className="verify-2fa-form-wrapper">
                        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }} style={{ marginTop: 'calc(var(--sp-lg)*1.886792)' }}>
                            <Col span={24}>
                                <MuiOtpInput
                                    length={6}
                                    value={otp}
                                    onChange={this.handleOtpChange}
                                    TextFieldsProps={{
                                        type: 'text',
                                        inputMode: 'numeric',
                                        autoComplete: 'one-time-code',
                                        placeholder: '.',
                                        sx: {
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '10px',
                                                backgroundColor: '#fff',
                                                width: '52px',
                                                height: '56px'
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderRadius: '10px',
                                                borderColor: 'rgba(103, 103, 103, 0.5)'
                                            },
                                            '& .MuiInputBase-input': {
                                                color: 'rgba(58, 58, 58, 1)',
                                                fontSize: '24px',
                                                fontWeight: 500,
                                                textAlign: 'center',
                                                padding: '12px'
                                            },
                                            '& .MuiInputBase-input::placeholder': {
                                                color: 'rgba(177, 177, 177, 1)',
                                                fontSize: '32px',
                                                fontWeight: 500,
                                                opacity: 1
                                            }
                                        }
                                    }}
                                    sx={{
                                        gap: '12px',
                                        '& .MuiOtpInput-TextField': {
                                            width: '52px'
                                        }
                                    }}
                                />
                            </Col>
                        </Row>

                        <Row style={{ marginTop: 'calc(var(--sp-lg)*1.5)' }}>
                            <Col span={24}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    <TextBase size={'var(--fs-body)'} color={Colors.black}>
                                        {translate('didntReceiveCode')}
                                    </TextBase>
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontWeight: 600,
                                            cursor: seconds === 0 && !resending ? 'pointer' : 'default',
                                            textDecoration: seconds === 0 && !resending ? 'underline' : 'none',
                                            color: seconds === 0 && !resending ? Colors.black : '#9e9e9e',
                                            fontSize: 'var(--fs-body)'
                                        }}
                                        onClick={this.handleResendOTP}
                                    >
                                        {seconds === 0
                                            ? translate('resendCode')
                                            : `${translate('resendCode')} (${seconds < 10 ? '00:0' + seconds : '00:' + seconds})`}
                                    </Typography>
                                </Box>
                            </Col>
                        </Row>
                    </div>

                    <div
                        style={{ marginBottom: 'calc(var(--sp-md2)*1.8)', marginTop: 'calc(var(--sp-lg)*2.358)' }}
                        className="flex-center-all max-width-100 login-button-wrapper"
                    >
                        <ButtonGradient
                            onClick={this.handleVerify}
                            textStyle={{ fontWeight: 700 }}
                            title="verifyAndLogin"
                            loading={submitting}
                            disabled={otp.length !== 6 || submitting}
                        />
                    </div>
                </div>

                {(submitting || resending) && <CustomSpinner fullPage top={0} />}
            </motion.div>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        userInfo: getValueFromObjectByKeys(state, ['User', 'userInfo'])
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        dispatch,
        verify2FAOTP: (params) => dispatch(UserAction.verify2FAOTP(params)),
        resend2FAOTP: (params) => dispatch(UserAction.resend2FAOTP(params)),
        saveBranding: () => dispatch(UserAction.saveBranding())
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(WithNavigate(withLocationState(Verify2FA)));
